import { useState, useContext } from 'react';
import { FaBell, FaTimes, FaEnvelope, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import HttpInterceptor from '../lib/HttpInterceptor';
import UserContext from './UserContext';

function TrainerAvailabilityNotificationModal({ 
  isOpen, 
  onClose, 
  trainerId, 
  trainerName, 
  programId = null, 
  programName = null,
  service = null
}) {
  const { session } = useContext(UserContext);
  const [email, setEmail] = useState(session?.email || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Allow both authenticated and unauthenticated users to request notifications
    // Authentication is optional - backend will handle it

    setIsSubmitting(true);
    try {
      const payload = {
        email: email.trim(),
      };
      
      if (trainerId) {
        payload.trainerId = trainerId;
      }
      if (programId) {
        payload.programId = programId;
      }
      if (service) {
        payload.service = service;
      }

      const response = await HttpInterceptor.post('/api/trainer-availability-notifications/request', payload);

      const successMessage = service 
        ? `You will be notified when ${service} trainers become available`
        : response.data.message || 'You will be notified when this trainer/program becomes available';
      
      toast.success(successMessage);
      onClose();
      setEmail('');
    } catch (error) {
      // Handle different error cases
      if (error.response?.status === 400 && error.response?.data?.data?.isNotified) {
        // User was already notified - show when they were notified
        const notifiedAt = error.response.data.data.notifiedAt;
        let notifiedDate = 'previously';
        if (notifiedAt) {
          const date = new Date(notifiedAt);
          notifiedDate = date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        }
        toast.error(`You were already notified about this trainer/program on ${notifiedDate}. You cannot request notifications again for the same trainer/program.`, {
          autoClose: 6000
        });
      } else {
        const errorMessage = error.response?.data?.message || 'Failed to request notification. Please try again.';
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const notificationText = programName
    ? `Get notified when "${programName}" by ${trainerName} becomes available`
    : `Get notified when ${trainerName} has programs available`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 sm:p-8 relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close modal"
        >
          <FaTimes className="text-xl" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <FaBell className="text-primary text-2xl" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Get Notified</h2>
          <p className="text-gray-600 text-sm">
            {service 
              ? `Get notified when ${service} trainers become available`
              : notificationText
            }
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                disabled={isSubmitting}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>How it works:</strong> We'll send you an email notification as soon as this trainer/program becomes available for enrollment.
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !email.trim()}
              className="flex-1 px-4 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <FaBell />
                  Notify Me
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default TrainerAvailabilityNotificationModal;

